import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@jsq/ds/core";
import { useGetTicketExportLazyQuery } from "#graphql/supergraphHooks";
import type { ExportStatus } from "#graphql/supergraphTypes";

const POLLING_INTERVAL = 2000;

type UseTicketExportPollingParams = {
  exportId: string | null;
  onStatusChange?: (status: ExportStatus) => void;
  enabled: boolean;
};

export const pollTicketExportStatus = async ({
  exportId,
  getTicketExportStatus,
  onStatusChange,
  stopPolling,
}: {
  exportId: string | null;
  getTicketExportStatus: ReturnType<typeof useGetTicketExportLazyQuery>[0];
  onStatusChange?: (status: ExportStatus) => void;
  stopPolling: () => void;
}): Promise<void> => {
  if (!exportId) {
    return;
  }

  const { data, error } = await getTicketExportStatus({ variables: { exportId } });

  if (error) {
    toast.error(error.message);
    stopPolling();
    return;
  }

  const job = data?.getTicketExport;
  if (!job) {
    toast.error("Export job not found");
    stopPolling();
    return;
  }

  if (onStatusChange) {
    onStatusChange(job.status);
  }

  switch (job.status) {
    case "READY": {
      if (job.downloadUrl) {
        toast.success("Export generated successfully.");
        window.open(job.downloadUrl, "_blank");
      } else {
        toast.error("Export ready but download link is missing.");
      }
      stopPolling();
      break;
    }
    case "ERROR": {
      toast.error(`Export failed: ${job.errorMessage ?? "Unknown error"}`);
      stopPolling();
      break;
    }
    case "PENDING": {
      break;
    }
    default: {
      const exhaustiveCheck: never = job.status;
      toast.error(`Unexpected export status: ${exhaustiveCheck as string}`);
      stopPolling();
      break;
    }
  }
};

export function useTicketExportPolling({
  exportId,
  onStatusChange,
  enabled,
}: UseTicketExportPollingParams) {
  const [isPolling, setIsPolling] = useState(false);

  const [getTicketExportStatus, { error: queryError }] = useGetTicketExportLazyQuery({
    fetchPolicy: "network-only",
  });

  const savedOnStatusChange = useRef(onStatusChange);

  useEffect(() => {
    savedOnStatusChange.current = onStatusChange;
  }, [onStatusChange]);

  const pollExportStatus = useCallback(async () => {
    await pollTicketExportStatus({
      exportId,
      getTicketExportStatus,
      onStatusChange: savedOnStatusChange.current ?? undefined,
      stopPolling: () => setIsPolling(false),
    });
  }, [exportId, getTicketExportStatus]);

  useEffect(() => {
    if (queryError) {
      toast.error(queryError.message);
      setIsPolling(false);
    }
  }, [queryError]);

  useEffect(() => {
    setIsPolling(enabled && !!exportId);
  }, [enabled, exportId]);

  useEffect(() => {
    if (isPolling) {
      const intervalId = setInterval(() => void pollExportStatus(), POLLING_INTERVAL);
      void pollExportStatus();
      return () => clearInterval(intervalId);
    }
  }, [isPolling, pollExportStatus]);

  return { isPollingInProgress: isPolling };
}
